/**
 * Template Builder Component
 *
 * Visual editor for creating WhatsApp message templates
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  Alert,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Stack,
  Tooltip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Send as SendIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  WhatsAppTemplate,
  WhatsAppTemplateCategory,
  TemplateCreateRequest,
  WhatsAppTemplateButton,
  WhatsAppTemplateHeader,
  WhatsAppConstraints
} from '../../types/whatsapp-templates';
import {
  fetchWhatsAppVariableMetadata,
  WhatsAppVariableMetadata,
  VariableMetadataItem,
  CustomVariableItem
} from '../../services/whatsapp-template-metadata-api';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';

interface TemplateBuilderProps {
  accountId?: number;
  channelId?: number;
  initialTemplate?: WhatsAppTemplate;
  onSave: (template: TemplateCreateRequest) => Promise<void>;
  onCancel: () => void;
  constraints?: WhatsAppConstraints;
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  accountId,
  channelId,
  initialTemplate,
  onSave,
  onCancel,
  constraints
}) => {
  // Variable metadata from backend (no hardcoding)
  const [variableMetadata, setVariableMetadata] = useState<WhatsAppVariableMetadata | null>(null);
  const [variableMetadataLoading, setVariableMetadataLoading] = useState(false);

  // Form state
  const [name, setName] = useState(initialTemplate?.name || '');
  const [category, setCategory] = useState<WhatsAppTemplateCategory>(
    initialTemplate?.category || WhatsAppTemplateCategory.UTILITY
  );
  const [language, setLanguage] = useState(initialTemplate?.language || 'en');
  const [body, setBody] = useState(initialTemplate?.body || '');
  const [footer, setFooter] = useState(initialTemplate?.footer || '');

  // Header state
  const [headerType, setHeaderType] = useState<'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>(
    initialTemplate?.header ? initialTemplate.header.type : 'NONE'
  );
  const [headerText, setHeaderText] = useState(initialTemplate?.header?.text || '');
  const [headerMediaUrl, setHeaderMediaUrl] = useState(initialTemplate?.header?.media_url || '');

  // Buttons state
  const [buttons, setButtons] = useState<WhatsAppTemplateButton[]>(
    initialTemplate?.buttons || []
  );

  // Variables state
  const [variables, setVariables] = useState<string[]>(initialTemplate?.variables || []);
  const [newVariable, setNewVariable] = useState('');

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Unique name check (provider-side)
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameCheckAvailable, setNameCheckAvailable] = useState<boolean | null>(null);
  const [nameCheckMessage, setNameCheckMessage] = useState<string>('');

  // Fetch variable metadata from backend when accountId is available
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setVariableMetadataLoading(true);
    fetchWhatsAppVariableMetadata(accountId)
      .then((data) => {
        if (!cancelled) setVariableMetadata(data);
      })
      .catch(() => {
        if (!cancelled) setVariableMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setVariableMetadataLoading(false);
      });
    return () => { cancelled = true; };
  }, [accountId]);

  // Convert display name to WhatsApp API name (preview)
  const convertToWhatsAppName = (displayName: string): string => {
    if (!displayName) return '';
    let apiName = displayName.toLowerCase();
    apiName = apiName.replace(/[\s-]+/g, '_'); // Replace spaces and dashes with underscores
    apiName = apiName.replace(/[^a-z0-9_]/g, ''); // Remove special characters
    apiName = apiName.replace(/_+/g, '_'); // Remove consecutive underscores
    apiName = apiName.replace(/^_|_$/g, ''); // Remove leading/trailing underscores
    if (apiName && !/^[a-z]/.test(apiName)) {
      apiName = 'template_' + apiName; // Ensure starts with a letter
    }
    return apiName.slice(0, 512);
  };

  const convertedName = convertToWhatsAppName(name);

  // Debounced unique name check against WhatsApp provider (when channelId and name are set)
  useEffect(() => {
    if (!channelId || !convertedName) {
      setNameCheckAvailable(null);
      setNameCheckMessage('');
      return;
    }
    const timer = setTimeout(async () => {
      setNameCheckLoading(true);
      setNameCheckAvailable(null);
      setNameCheckMessage('');
      try {
        const res = await whatsappTemplatesApi.checkTemplateNameAvailable(
          channelId,
          convertedName,
          language || 'en',
          initialTemplate?.id
        );
        setNameCheckAvailable(res.available);
        setNameCheckMessage(res.message || '');
      } catch {
        setNameCheckAvailable(false);
        setNameCheckMessage('Could not verify name with WhatsApp.');
      } finally {
        setNameCheckLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [channelId, convertedName, language, initialTemplate?.id]);

  // Default constraints
  const defaultConstraints: WhatsAppConstraints = {
    header: {
      text_max_length: 60,
      media_types: ['IMAGE', 'VIDEO', 'DOCUMENT']
    },
    body: {
      max_length: 1024,
      max_variables: 10
    },
    footer: {
      max_length: 60,
      allow_variables: false
    },
    buttons: {
      max_count: 3,
      max_text_length: 25,
      allowed_types: ['QUICK_REPLY', 'URL', 'PHONE_NUMBER']
    },
    variables: {
      max_total: 10,
      format: '{{N}}'
    }
  };

  const activeConstraints = constraints || defaultConstraints;

  // Extract variables from body text
  useEffect(() => {
    const variablePattern = /\{\{(\d+)\}\}/g;
    const matches = body.match(variablePattern);
    if (matches) {
      const count = matches.length;
      const newVars = Array.from({ length: count }, (_, i) => `var_${i + 1}`);
      setVariables(prev => {
        // Preserve existing variable names where possible
        return newVars.map((v, i) => prev[i] || v);
      });
    }
  }, [body]);

  // Generate preview
  useEffect(() => {
    let previewText = '';

    if (headerType === 'TEXT' && headerText) {
      previewText += `*${headerText}*\n\n`;
    } else if (headerType !== 'NONE' && headerType !== 'TEXT') {
      previewText += `[${headerType}]\n\n`;
    }

    let bodyPreview = body;
    variables.forEach((varName, index) => {
      const placeholder = `{{${index + 1}}}`;
      bodyPreview = bodyPreview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), `*${varName}*`);
    });
    previewText += bodyPreview;

    if (footer) {
      previewText += `\n\n_${footer}_`;
    }

    if (buttons.length > 0) {
      previewText += '\n\n';
      buttons.forEach((btn, index) => {
        previewText += `\n[${index + 1}] ${btn.text}`;
      });
    }

    setPreview(previewText);
  }, [body, footer, headerType, headerText, variables, buttons]);

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name || name.length === 0) {
      newErrors.name = 'Template name is required';
    } else if (!convertedName) {
      newErrors.name = 'Template name must contain at least one letter or number';
    }

    if (!body || body.length === 0) {
      newErrors.body = 'Message body is required';
    } else if (body.length > activeConstraints.body.max_length) {
      newErrors.body = `Body exceeds maximum length of ${activeConstraints.body.max_length} characters`;
    }

    if (headerType === 'TEXT' && headerText.length > activeConstraints.header.text_max_length) {
      newErrors.header = `Header text exceeds maximum length of ${activeConstraints.header.text_max_length} characters`;
    }

    if (footer && footer.length > activeConstraints.footer.max_length) {
      newErrors.footer = `Footer exceeds maximum length of ${activeConstraints.footer.max_length} characters`;
    }

    if (buttons.length > activeConstraints.buttons.max_count) {
      newErrors.buttons = `Maximum ${activeConstraints.buttons.max_count} buttons allowed`;
    }

    buttons.forEach((btn, index) => {
      if (btn.text.length > activeConstraints.buttons.max_text_length) {
        newErrors[`button_${index}`] = `Button text exceeds ${activeConstraints.buttons.max_text_length} characters`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers: insert variable by name (from metadata) – appends {{n}} and adds name to variables array
  const handleInsertVariableByName = (variableName: string) => {
    if (variables.length >= activeConstraints.body.max_variables) return;
    const variableNumber = variables.length + 1;
    const placeholder = `{{${variableNumber}}}`;
    setBody(prev => prev + placeholder);
    setVariables(prev => [...prev, variableName]);
  };

  // Insert variable placeholder only (generic position) – keeps body and variables in sync with a generic name
  const handleInsertVariable = () => {
    if (variables.length >= activeConstraints.body.max_variables) return;
    const variableNumber = variables.length + 1;
    const placeholder = `{{${variableNumber}}}`;
    setBody(prev => prev + placeholder);
    setVariables(prev => [...prev, `variable_${variableNumber}`]);
  };

  // Remove variable at index and renumber body so placeholders stay {{1}}, {{2}}, ... (Dialog360 requirement)
  const handleRemoveVariable = (index: number) => {
    const placeholderToRemove = `{{${index + 1}}}`;
    const rePlaceholder = new RegExp(placeholderToRemove.replace(/[{}]/g, '\\$&'), 'g');
    setBody(prev => {
      const without = prev.replace(rePlaceholder, '');
      const matches = [...without.matchAll(/\{\{\d+\}\}/g)];
      if (matches.length === 0) return without.replace(/\s+/g, ' ').trim();
      let result = without;
      for (let i = matches.length - 1; i >= 0; i--) {
        const m = matches[i];
        const start = m.index!;
        const end = start + m[0].length;
        result = result.slice(0, start) + `{{${i + 1}}}` + result.slice(end);
      }
      return result.replace(/\s+/g, ' ').trim();
    });
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddButton = () => {
    if (buttons.length < activeConstraints.buttons.max_count) {
      setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }]);
    }
  };

  const handleRemoveButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleButtonChange = (index: number, field: string, value: any) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const handleSave = async () => {
    setSaveError(null);

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      const templateData: TemplateCreateRequest = {
        name,
        category,
        language,
        body,
        footer: footer || undefined,
        variables,
        header: headerType !== 'NONE' ? {
          type: headerType,
          text: headerType === 'TEXT' ? headerText : undefined,
          media_url: headerType !== 'TEXT' ? headerMediaUrl : undefined
        } : undefined,
        buttons: buttons.length > 0 ? buttons : undefined
      };

      await onSave(templateData);
    } catch (error: any) {
      console.error('Error saving template:', error);
      // Display the error message (API service extracts detail from backend response)
      setSaveError(error?.message || 'Failed to save template. Please check your input and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Error Alert */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Panel - Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>

            <Stack spacing={3}>
              {/* Basic Info */}
              <TextField
                fullWidth
                label="Template Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={
                  errors.name ||
                  (nameCheckMessage && !nameCheckLoading ? nameCheckMessage : null) ||
                  (name && convertedName
                    ? `WhatsApp API name: ${convertedName}. The check does not submit the template.`
                    : 'Enter any name - it will be auto-converted for WhatsApp')
                }
                placeholder="RentRemote Welcome Message"
                InputProps={{
                  endAdornment: channelId && (name || nameCheckLoading) ? (
                    <InputAdornment position="end">
                      {nameCheckLoading ? (
                        <CircularProgress size={24} />
                      ) : nameCheckAvailable === true ? (
                        <Tooltip title="Name is available for submission on WhatsApp">
                          <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                        </Tooltip>
                      ) : nameCheckAvailable === false ? (
                        <Tooltip title={nameCheckMessage || 'Name already in use on WhatsApp'}>
                          <ErrorIcon color="error" sx={{ fontSize: 28 }} />
                        </Tooltip>
                      ) : null}
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value as WhatsAppTemplateCategory)}
                      label="Category"
                    >
                      <MenuItem value={WhatsAppTemplateCategory.UTILITY}>Utility</MenuItem>
                      <MenuItem value={WhatsAppTemplateCategory.MARKETING}>Marketing</MenuItem>
                      <MenuItem value={WhatsAppTemplateCategory.AUTHENTICATION}>Authentication</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      label="Language"
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      <MenuItem value="pt">Portuguese</MenuItem>
                      <MenuItem value="hi">Hindi</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider />

              {/* Header Section */}
              <Box>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Header (Optional)</FormLabel>
                  <RadioGroup
                    row
                    value={headerType}
                    onChange={(e) => setHeaderType(e.target.value as any)}
                  >
                    <FormControlLabel value="NONE" control={<Radio />} label="None" />
                    <FormControlLabel value="TEXT" control={<Radio />} label="Text" />
                    <FormControlLabel value="IMAGE" control={<Radio />} label="Image" />
                    <FormControlLabel value="VIDEO" control={<Radio />} label="Video" />
                    <FormControlLabel value="DOCUMENT" control={<Radio />} label="Document" />
                  </RadioGroup>
                </FormControl>

                {headerType === 'TEXT' && (
                  <TextField
                    fullWidth
                    label="Header Text"
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    error={!!errors.header}
                    helperText={errors.header || `Max ${activeConstraints.header.text_max_length} characters`}
                    sx={{ mt: 2 }}
                  />
                )}

                {headerType !== 'NONE' && headerType !== 'TEXT' && (
                  <TextField
                    fullWidth
                    label="Media URL"
                    value={headerMediaUrl}
                    onChange={(e) => setHeaderMediaUrl(e.target.value)}
                    placeholder={headerType === 'VIDEO' ? 'https://example.com/video.mp4' : headerType === 'IMAGE' ? 'https://example.com/image.jpg' : 'https://example.com/document.pdf'}
                    helperText="Must be a direct link to the file (e.g. .mp4, .jpg). YouTube, Vimeo, or other page links are not supported for template submission."
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>

              <Divider />

              {/* Body Section */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Message Body *</Typography>
                  <Tooltip title="Insert variable placeholder (generic)">
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleInsertVariable}
                      disabled={variables.length >= activeConstraints.body.max_variables}
                    >
                      Insert Variable
                    </Button>
                  </Tooltip>
                </Box>

                {/* Available variables from backend metadata (no hardcoding) */}
                {(variableMetadata || variableMetadataLoading) && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      Available variables (click to insert)
                    </Typography>
                    {variableMetadataLoading ? (
                      <Typography variant="caption" color="text.secondary">Loading variables…</Typography>
                    ) : variableMetadata ? (
                      <>
                        {variableMetadata.system_variables?.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                              System
                            </Typography>
                            <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {variableMetadata.system_variables.map((v: VariableMetadataItem) => (
                                <Tooltip key={v.name} title={v.label || v.name}>
                                  <Chip
                                    size="small"
                                    label={v.name}
                                    variant="outlined"
                                    onClick={() => handleInsertVariableByName(v.name)}
                                    sx={{ cursor: 'pointer' }}
                                    disabled={variables.length >= activeConstraints.body.max_variables}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {variableMetadata.custom_variables?.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="secondary" sx={{ fontWeight: 600 }}>
                              Custom
                            </Typography>
                            <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {variableMetadata.custom_variables.map((v: CustomVariableItem) => (
                                <Tooltip key={v.name} title={v.label || v.name}>
                                  <Chip
                                    size="small"
                                    label={v.name}
                                    color="secondary"
                                    variant="outlined"
                                    onClick={() => handleInsertVariableByName(v.name)}
                                    sx={{ cursor: 'pointer' }}
                                    disabled={variables.length >= activeConstraints.body.max_variables}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {(!variableMetadata.system_variables?.length && !variableMetadata.custom_variables?.length) && (
                          <Typography variant="caption" color="text.secondary">No variables defined. Use “Insert Variable” above or add custom variables in account settings.</Typography>
                        )}
                      </>
                    ) : null}
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  error={!!errors.body}
                  helperText={
                    errors.body ||
                    `${body.length}/${activeConstraints.body.max_length} characters. Use {{1}}, {{2}} for variables.`
                  }
                  placeholder="Hi {{1}}, your order #{{2}} is ready for pickup!"
                />

                {variables.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Variables:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {variables.map((varName, index) => (
                        <Chip
                          key={index}
                          label={`{{${index + 1}}} = ${varName}`}
                          size="small"
                          variant="outlined"
                          onDelete={() => handleRemoveVariable(index)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Footer Section */}
              <TextField
                fullWidth
                label="Footer (Optional)"
                value={footer}
                onChange={(e) => setFooter(e.target.value)}
                error={!!errors.footer}
                helperText={errors.footer || `Max ${activeConstraints.footer.max_length} characters`}
                placeholder="Reply STOP to unsubscribe"
              />

              <Divider />

              {/* Buttons Section */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Buttons (Optional)</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddButton}
                    disabled={buttons.length >= activeConstraints.buttons.max_count}
                  >
                    Add Button
                  </Button>
                </Box>

                {buttons.map((button, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <Box flex={1}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={button.type}
                                  onChange={(e) => handleButtonChange(index, 'type', e.target.value)}
                                  label="Type"
                                >
                                  <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                                  <MenuItem value="URL">URL</MenuItem>
                                  <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Button Text"
                                value={button.text}
                                onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                error={!!errors[`button_${index}`]}
                                helperText={errors[`button_${index}`]}
                              />
                            </Grid>

                            {button.type === 'URL' && (
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="URL"
                                  value={button.url || ''}
                                  onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                                  placeholder="https://example.com"
                                />
                              </Grid>
                            )}

                            {button.type === 'PHONE_NUMBER' && (
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Phone Number"
                                  value={button.phone_number || ''}
                                  onChange={(e) => handleButtonChange(index, 'phone_number', e.target.value)}
                                  placeholder="+1234567890"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveButton(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {buttons.length === 0 && (
                  <Alert severity="info">
                    No buttons added. Buttons are optional but can improve engagement.
                  </Alert>
                )}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : initialTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Right Panel - Preview */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PreviewIcon color="primary" />
              <Typography variant="h6">Preview</Typography>
            </Box>

            <Card
              sx={{
                bgcolor: '#E3F2FD',
                borderRadius: 2,
                p: 2,
                minHeight: 300
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    lineHeight: 1.5
                  }}
                >
                  {preview || 'Your template preview will appear here...'}
                </Typography>
              </CardContent>
            </Card>

            <Box mt={3}>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Template Info:
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Chip label={`Category: ${category}`} size="small" variant="outlined" />
                <Chip label={`Language: ${language}`} size="small" variant="outlined" />
                <Chip
                  label={`Variables: ${variables.length}/${activeConstraints.body.max_variables}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Buttons: ${buttons.length}/${activeConstraints.buttons.max_count}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateBuilder;
